import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import {
  AutoAwesomeOutlined,
  CloudUploadOutlined,
  LinkOutlined,
  MicOutlined,
  PersonOutlined,
} from "@mui/icons-material";
import { mediaSrc } from "../../utils/mediaSrc";
import ProductAdsCategoryField from "./ProductAdsCategoryField";

export default function ProductAdsStudioStep({
  lang,
  theme,
  pickerCardSx,
  engine,
  wizardMeta,
  GENERATION_ENGINES,
  productFile,
  uploading,
  previewUrl,
  ad,
  productLink,
  setProductLink,
  setProductFile,
  setPreviewUrl,
  handleUpload,
  setConfig,
  setPicker,
  selectedInfluencer,
  selectedAvatar,
  selectedVoice,
  generatingScript,
  handleGenerateScript,
  form,
  setForm,
  cfg,
  isNarrow,
  categories,
  categoriesLoading,
  categoryId,
  onCategoryChange,
}) {
  return (
    <Stack spacing={2.5}>
      <ProductAdsCategoryField
        lang={lang}
        categories={categories}
        loading={categoriesLoading}
        value={categoryId}
        onChange={onCategoryChange}
      />

      <Alert severity="info">
        {lang?.productAdsStudioIntro ||
          "Add your product (photo or link), pick who presents it, then write or AI-generate your script."}
      </Alert>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" fontWeight={700}>
              {lang?.productAdsProductSource || "Product"}
            </Typography>
            <Button variant="outlined" component="label" startIcon={<CloudUploadOutlined />} fullWidth>
              {lang?.productAdsChooseFile || "Upload product photo"}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setProductFile(f);
                  if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(URL.createObjectURL(f));
                }}
              />
            </Button>
            {(previewUrl || ad?.media_url) && (
              <Box
                component="img"
                src={previewUrl || mediaSrc(ad.media_url)}
                alt="Product"
                sx={{ width: "100%", maxHeight: 160, borderRadius: 2, objectFit: "contain" }}
              />
            )}
            <Button variant="contained" disabled={!productFile || uploading} onClick={handleUpload}>
              {uploading ? <CircularProgress size={22} /> : lang?.productAdsUpload || "Save upload"}
            </Button>
            <Divider>{lang?.productAdsOr || "or"}</Divider>
            <TextField
              label={lang?.productAdsProductLink || "Product link"}
              placeholder="https://…"
              fullWidth
              value={productLink}
              onChange={(e) => setProductLink(e.target.value)}
              InputProps={{
                startAdornment: <LinkOutlined sx={{ mr: 1, color: "text.secondary" }} />,
              }}
            />
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>{lang?.productAdsEngine || "Video engine"}</InputLabel>
            <Select
              label={lang?.productAdsEngine || "Video engine"}
              value={engine}
              onChange={(e) => {
                const v = e.target.value;
                setConfig("generation_engine", v);
                if (v === "heygen_agent") setConfig("influencer_id", null);
              }}
            >
              {GENERATION_ENGINES.map((eng) => {
                if (eng.id === "veo_showcase" && wizardMeta.showcase_available === false) return null;
                return (
                  <MenuItem key={eng.id} value={eng.id}>
                    {lang?.[eng.labelKey] || eng.id}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>
            {lang?.productAdsCastRowTitle || "Cast & voice"}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
            {engine === "veo_showcase" ? (
              <Box
                component="button"
                type="button"
                onClick={() => setPicker("creator")}
                sx={{
                  ...pickerCardSx,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  font: "inherit",
                  color: "inherit",
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    overflow: "hidden",
                    flexShrink: 0,
                    bgcolor: "grey.800",
                  }}
                >
                  {selectedInfluencer?.photo_url ? (
                    <Box
                      component="img"
                      src={mediaSrc(selectedInfluencer.photo_url)}
                      alt=""
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <PersonOutlined />
                    </Box>
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontWeight={800} noWrap>
                    {selectedInfluencer?.name || lang?.productAdsPickCreator || "Choose creator"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lang?.productAdsCreatorLabel || "Creator + product"}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setPicker("avatar")}
                  sx={{
                    ...pickerCardSx,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    font: "inherit",
                    color: "inherit",
                  }}
                >
                  <Box sx={{ width: 48, height: 48, borderRadius: "50%", overflow: "hidden", bgcolor: "grey.800" }}>
                    {selectedAvatar?.preview_image_url ? (
                      <Box
                        component="img"
                        src={selectedAvatar.preview_image_url}
                        alt=""
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                        }}
                      >
                        <PersonOutlined />
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={800} noWrap>
                      {selectedAvatar?.avatar_name ||
                        selectedAvatar?.name ||
                        lang?.productAdsAuto ||
                        "Auto avatar"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {lang?.videoAgentPickerAvatar || "Avatar"}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setPicker("voice")}
                  sx={{
                    ...pickerCardSx,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    font: "inherit",
                    color: "inherit",
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MicOutlined color="primary" />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={800} noWrap>
                      {selectedVoice?.name || lang?.productAdsAuto || "Auto voice"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {lang?.videoAgentPickerVoice || "Voice"}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Stack>
        </Grid>
      </Grid>

      <Divider />

      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
        <Button
          variant="contained"
          startIcon={<AutoAwesomeOutlined />}
          disabled={
            generatingScript || (!ad?.media_url && !String(productLink || cfg.product_url || "").trim())
          }
          onClick={handleGenerateScript}
        >
          {generatingScript
            ? lang?.generating || "Generating…"
            : lang?.productAdsGenScript || "Generate script with AI"}
        </Button>
      </Stack>

      <TextField
        label={lang?.productAdsScript || "Ad script (teleprompter)"}
        fullWidth
        multiline
        minRows={isNarrow ? 7 : 9}
        value={form.script}
        onChange={(e) => setForm((f) => ({ ...f, script: e.target.value }))}
        placeholder={lang?.productAdsScriptPlaceholder || "Paste your script or generate from your product…"}
        helperText={
          lang?.productAdsScriptStudioHint ||
          "AI reads your product photo or link. Edit freely — this is what will be spoken in the video."
        }
      />
    </Stack>
  );
}
